<script setup>
import TableSort from './TableSort.vue'
import TableCell from './TableCell.vue'
import {toRefs, computed} from 'vue'

defineEmits([
  'sort',
  'click',
])

const props = defineProps({
  isSortable: {
    type: Boolean,
    default: false,
  },
  sortProp: {
    type: String,
    default: '',
  },
  currentSort: {
    type: String,
    default: '',
  },
  defaultDirection: {
    type: String,
    default: '',
  },
  id: {
    type: String,
    default: '',
  },
  isClickable: {
    type: Boolean,
    default: false,
  },
})

//Get refs
const {isSortable, currentSort, sortProp, defaultDirection, id} = toRefs(props)

// Auto-generate ID if not provided
const headerId = computed(() => {
  if (id.value) {
    return id.value
  }
  if (sortProp.value) {
    return `header-${sortProp.value}`
  }
  // Generate a unique ID based on slot content or position
  return `header-${Math.random().toString(36).substr(2, 9)}`
})

// Compute aria-sort value
const ariaSort = computed(() => {
  if (!isSortable.value) {
    return undefined
  }
  if (!currentSort.value) {
    return 'none'
  }
  const isActive = currentSort.value.replace(/^-/, '') === sortProp.value
  if (!isActive) {
    return 'none'
  }
  const isDesc = currentSort.value.match(/^-/)
  return isDesc ? 'descending' : 'ascending'
})
</script>

<template>
  <TableCell
    :id="headerId"
    is-header-cell
    :aria-sort="ariaSort"
    scope="col"
    @click="isClickable && $emit('click', $event)"
  >
    <TableSort
      v-if="isSortable"
      :current-sort="currentSort"
      :prop="sortProp"
      :default-direction="defaultDirection"
      @sort="$emit('sort', $event)"
    >
      <div :class="$style.content">
        <slot />
      </div>
    </TableSort>
    <div
      v-else
      :class="$style.content"
    >
      <slot />
    </div>
  </TableCell>
</template>

<style module lang="scss">
.content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-text-muted);
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 0.025em;
  font-size: 0.75rem;
}
</style>
